import {
  minWidthQuery, getObserver, debounce,
  createSlider,
  destroySlider,
  handleTablistKeydown,
  setTablistRovingState,
  cocoFetchInit,
  getVisibleFocusable,
  getShippingCalculatorStrings,
  FOCUSABLE_SELECTOR,
  lockScroll,
  unlockScroll,
} from '@theme/coco-utilities';

import Motion from '@lib/motion';
import { emitCocoEvent, COCO_EVENTS } from '@theme/theme-events';

window.Motion = Motion;
window.addEventListener('DOMContentLoaded', () => {
  document.body.classList.add('loaded');
  document.dispatchEvent(new CustomEvent('page:loaded'));
});

// Ensure the global `theme` namespace exists with shapes used across the theme.
window.theme = window.theme || {};
window.theme.config = window.theme.config || {};
window.theme.a11y = {
  getVisibleFocusable,
  FOCUSABLE_SELECTOR,
};

(() => {
  const reducedMotionMq =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  window.theme.config.motionReduced = !!reducedMotionMq?.matches;
  window.theme.config.isTouch =
    'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;

  reducedMotionMq?.addEventListener?.('change', (event) => {
    window.theme.config.motionReduced = event.matches;
  });
})();

const onYouTubePromise = new Promise((resolve) => {
  if (window.YT?.Player) {
    queueMicrotask(resolve);
    return;
  }
  const previous = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    if (typeof previous === 'function') previous();
    resolve();
  };
});

class CocoLazyMedia extends HTMLElement {
  constructor() {
    super();

    this._viewportIntersecting = false;

    this.addEventListener('video:play', () => {
      // User-clicked play must not be cancelled by viewport/swiper timing
      // (common in quick view where slides exist before Swiper marks one active).
      if (this._activePlayback) return;
      if (this.canPlayInCurrentContext()) return;
      this.pause();
    });
  }

  connectedCallback() {
    if (!this._interactionBound) {
      this._onPlayOverlayInteraction = this.onPlayOverlayClick.bind(this);
      this._onPlayOverlayKeydownBound = this.onPlayOverlayKeydown.bind(this);

      const deferredPoster = this.querySelector('[id^="coco-media-poster-"]');
      const playOverlay = this.querySelector('[data-action="play"]');
      const posterControl = deferredPoster || playOverlay;

      if (posterControl) {
        posterControl.addEventListener('click', this._onPlayOverlayInteraction);
        posterControl.addEventListener('keydown', this._onPlayOverlayKeydownBound);
        this._playOverlayEl = posterControl;
      }

      this._interactionBound = true;
    }

    const pauseButton = this.closest('.shopify-section')?.querySelector('[data-video-pause-button]');
    if (pauseButton) {
      pauseButton.addEventListener('click', this.onPauseButtonClick.bind(this));
      if (pauseButton.tagName !== 'BUTTON') {
        pauseButton.addEventListener('keydown', this.onPauseButtonKeydown.bind(this));
      }
    }
    const playButton = this.closest('.shopify-section')?.querySelector('[data-video-play-button]');
    if (playButton) {
      playButton.addEventListener('click', this.onPlayButtonClick.bind(this));
      if (playButton.tagName !== 'BUTTON') {
        playButton.addEventListener('keydown', this.onPlayButtonKeydown.bind(this));
      }
    }

    this.syncPlaybackButtons(this.playing || (this.autoplay && !this.paused));

    this.observeViewport();
    this.scheduleCarouselAutoplay();
  }

  isInActiveSwiperSlide() {
    const slide = this.closest('.swiper-slide');
    if (!slide) return false;
    if (slide.classList.contains('swiper-slide-duplicate')) return false;
    return slide.classList.contains('swiper-slide-active');
  }

  /**
   * Match grid-mix behavior: only play when visible in the viewport.
   * Carousel layouts also require the active slide (once Swiper has initialized),
   * unless the swiper opts in with `data-keep-videos-playing` (multi-visible galleries).
   */
  canPlayInCurrentContext() {
    if (!this.isConnected) return false;

    // IntersectionObserver flag only — no getBoundingClientRect (forced reflow / Lighthouse)
    if (this._viewportIntersecting !== true) return false;

    const slide = this.closest('.swiper-slide');
    if (!slide) return true;

    const swiperRoot = slide.closest('.swiper');
    // Instagram-style / multi-visible carousels: any on-screen video may play.
    if (swiperRoot?.hasAttribute('data-keep-videos-playing')) return true;

    // Markup always has `.swiper-slide`, but `.swiper-slide-active` only exists
    // after Swiper boots. Until then, allow playback (quick view / deferred init).
    if (!swiperRoot?.querySelector('.swiper-slide-active')) return true;

    return this.isInActiveSwiperSlide();
  }

  scheduleCarouselAutoplay() {
    if (!this.autoplay || this.paused) return;
    if (!this.canPlayInCurrentContext()) return;

    const tryPlay = () => {
      if (!this.isConnected || this.paused || this.playing) return;
      if (!this.canPlayInCurrentContext()) return;
      this._viewportIntersecting = true;
      this.play();
    };

    // Custom element / Swiper layout can settle after first paint.
    requestAnimationFrame(() => {
      tryPlay();
      requestAnimationFrame(tryPlay);
    });
    clearTimeout(this._carouselAutoplayTimer);
    this._carouselAutoplayTimer = setTimeout(tryPlay, 120);
  }

  observeViewport() {
    if (!('IntersectionObserver' in window) || this._viewportObserver) {
      // No IO — assume visible without layout read (rare legacy browsers)
      this._viewportIntersecting = true;
      if (this.autoplay && !this.paused && this.canPlayInCurrentContext()) {
        this.play();
      }
      return;
    }

    // Start false; IO sets the flag — never sync-measure with getBoundingClientRect
    this._viewportIntersecting = false;

    this._viewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === this) {
            this.onViewportChange(entry);
          }
        }
      },
      { threshold: 0.01 }
    );

    this._viewportObserver.observe(this);

    if (this.autoplay && !this.paused && this.canPlayInCurrentContext()) {
      this.play();
    }
  }

  onViewportChange(entry) {
    this._viewportIntersecting = entry.isIntersecting;

    if (entry.isIntersecting && this.canPlayInCurrentContext()) {
      if (this.autoplay && !this.paused) {
        this.play();
      }
      return;
    }

    // Same as grid-mix: pause whenever the media leaves the viewport,
    // including vertical-carousel / stacked / columns layouts.
    if (this.playing) {
      this.pause();
    }
  }

  syncPlaybackButtons(isPlaying) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const playButton = section.querySelector('[data-video-play-button]');
    const pauseButton = section.querySelector('[data-video-pause-button]');
    if (!playButton && !pauseButton) return;

    playButton?.classList.toggle('hidden', Boolean(isPlaying));
    pauseButton?.classList.toggle('hidden', !isPlaying);
  }

  onPlayButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.playing) {
      this.onPauseButtonClick(event);
      return;
    }
    this.paused = false;
    this._activePlayback = true;
    this.setPosterHidden(true);
    this.play();
    this.syncPlaybackButtons(true);
    this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
  }
  onPlayButtonKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.onPlayButtonClick(event);
  }
  onPauseButtonKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.onPauseButtonClick(event);
  }

  disconnectedCallback() {
    if (this._interactionBound) {
      if (this._playOverlayEl && this._onPlayOverlayInteraction) {
        this._playOverlayEl.removeEventListener('click', this._onPlayOverlayInteraction);
        this._playOverlayEl.removeEventListener('keydown', this._onPlayOverlayKeydownBound);
      }
      this._playOverlayEl = null;
      this._interactionBound = false;
    }

    clearTimeout(this._carouselAutoplayTimer);
    this._carouselAutoplayTimer = null;
    this._viewportObserver?.disconnect();
    this._viewportObserver = null;
    this._viewportIntersecting = false;
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls')
      ? document.getElementById(this.getAttribute('aria-controls'))
      : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay') || this.dataset.autoplay === 'true';
  }

  pauseOtherVideos() {
    if (this._pausingOthers) {
      return;
    }

    this._pausingOthers = true;

    try {
      const keepRoot = this.closest('[data-keep-videos-playing]');
      const scope =
        this.closest('product-info, [data-product-gallery], .shopify-section') ||
        document;

      scope.querySelectorAll('video-media').forEach((video) => {
        if (video === this) {
          return;
        }

        // Media gallery (multi-visible carousels): keep sibling videos playing.
        if (keepRoot && keepRoot.contains(video)) {
          return;
        }

        // Desktop/mobile variants in the same slide: ignore hidden peers.
        if (window.getComputedStyle(video).display === 'none') {
          return;
        }

        const nativePlayer = video.querySelector('video');
        const hasPlayer = Boolean(video.playerProxy);
        const isPlaying =
          video.playing ||
          hasPlayer ||
          (nativePlayer && !nativePlayer.paused);

        if (!isPlaying) {
          return;
        }

        // Pause playback only — keep `paused` for intentional user pauses so
        // carousel autoplay videos can resume on the next active slide.
        video.pause();
        video._activePlayback = false;
      });
    } finally {
      this._pausingOthers = false;
    }
  }

  get loop() {
    return this.dataset.loop === 'true';
  }

  get posterOverlay() {
    return (
      this.querySelector('[data-video-poster-button]') ||
      this.querySelector('[id^="coco-media-poster-"]') ||
      this.querySelector('[data-deferred-poster]') ||
      this.querySelector('[data-action="play"]')
    );
  }

  getPosterImages() {
    return Array.from(
      this.querySelectorAll(
        ':scope > img, :scope > .placeholder-svg, .video-poster, [data-media-card] > img, [data-media-card] > .placeholder-svg',
      ),
    );
  }

  setPosterHidden(hidden) {
    const poster = this.posterOverlay;

    if (hidden) {
      this.setAttribute('data-poster-hidden', '');
    } else {
      this.removeAttribute('data-poster-hidden');
    }

    if (poster) {
      poster.classList.toggle('hidden', hidden);
      poster.hidden = hidden;
      poster.setAttribute('aria-hidden', hidden ? 'true' : 'false');

      for (const child of poster.children) {
        child.classList.toggle('hidden', hidden);
        child.hidden = hidden;
      }
    }

    // Preview <img> must hide on click (product / featured / quick view),
    // not only the play overlay — media may still be loading from <template>.
    for (const image of this.getPosterImages()) {
      if (poster?.contains(image)) continue;
      image.classList.toggle('hidden', hidden);
      image.hidden = hidden;
      image.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    }
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(val) {
    if (val) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  get player() {
    if (this.playerProxy) {
      return this.playerProxy;
    }

    const target = this.playerTarget();
    if (target == null || typeof target !== 'object') {
      this.playerProxy = {
        play: () => { },
        pause: () => { }
      };
      return this.playerProxy;
    }

    this.playerProxy = new Proxy(target, {
      get: (playerTarget, prop) => {
        return async () => {
          const resolved = await playerTarget;
          if (resolved) {
            this.playerHandler(resolved, prop);
          }
        };
      }
    });
    return this.playerProxy;
  }

  static get observedAttributes() {
    return ['playing'];
  }

  onPlayOverlayKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.onPlayOverlayClick(event);
  }

  onPlayOverlayClick(event) {
    if (event.target.closest?.('a[href]')) {
      return;
    }
    this.onPosterClick(event);
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.playing) {
      this.paused = true;
      this._activePlayback = false;
      this.setPosterHidden(true);
      this.pause();
    } else {
      this.paused = false;
      this._activePlayback = true;
      this.setPosterHidden(true);
      this.play();
    }
  }

  onPauseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.paused = true;
    this._activePlayback = false;
    this.setPosterHidden(false);
    this.pause();
    this.syncPlaybackButtons(false);
    this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
  }

  play() {
    const hasPlayerContent = Boolean(
      this.querySelector('template')?.content?.querySelector('video, iframe') ||
      this.querySelector('video, iframe')
    );
    if (!hasPlayerContent) {
      this.setPosterHidden(false);
      return;
    }
    this.setPosterHidden(true);
    if (!this.playing) {
      this.pauseOtherVideos();
      this.player.play();
    }
  }

  pause() {
    // `playing` is set asynchronously; out-of-view pause must still reach the player.
    this.player.pause();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      const isPlaying = newValue === '';
      this.syncPlaybackButtons(isPlaying);

      if (isPlaying) {
        this.setPosterHidden(true);
        this.paused = false;
        this._activePlayback = true;
        this.pauseOtherVideos();
      } else if (this.paused) {
        this.setPosterHidden(false);
      }

      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
      }

      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
      }
    }
  }
}

class VideoMedia extends CocoLazyMedia {
  constructor() {
    super();
  }

  insertMediaContent(mediaCard, content) {
    const posterButton = mediaCard.querySelector(
      '.video-poster-button, .video-button, [data-action="play"], .deferred-poster, [id^="coco-media-poster-"]',
    );

    if (posterButton) {
      mediaCard.insertBefore(content, posterButton);
    } else {
      mediaCard.appendChild(content);
    }
  }

  playerTarget() {
    const host = this.getAttribute('host');
    if (host === 'youtube' || host === 'vimeo') {
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          const container = this.querySelector('[data-media-card]') || this;
          const content = templateElement.content.cloneNode(true);
          this.insertMediaContent(container, content);
          templateElement.remove();
        }

        const muteVideo = this.autoplay;

        const script = document.createElement('script');
        script.type = 'text/javascript';

        if (host === 'youtube') {
          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }

          await onYouTubePromise;

          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (
                  event.data === YT.PlayerState.PLAYING ||
                  event.data === YT.PlayerState.BUFFERING
                ) {
                  this.pauseOtherVideos();
                  this.setAttribute('playing', '');
                } else if (
                  event.data === YT.PlayerState.ENDED ||
                  event.data === YT.PlayerState.PAUSED
                ) {
                  this.removeAttribute('playing');
                }
              }
            }
          });
        }

        if (host === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }

          const player = new Vimeo.Player(this.querySelector('iframe'));

          if (muteVideo) {
            player.setMuted(true);
          }

          if (this.loop) {
            player.setLoop(true);
          }

          player.on('play', () => {
            this.pauseOtherVideos();
            this.setAttribute('playing', '');
          });

          player.on('pause', () => this.removeAttribute('playing'));
          player.on('ended', () => this.removeAttribute('playing'));

          resolve(player);
        }
      });
    } else {
      const templateElement = this.querySelector('template');
      if (templateElement) {
        const container = this.querySelector('[data-media-card]') || this;
        const content = templateElement.content.cloneNode(true);
        this.insertMediaContent(container, content);
        templateElement.remove();
      }

      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      const player = this.querySelector('video');

      if (player) {
        if (this.autoplay) {
          player.autoplay = true;
          player.muted = true;
        }

        if (this.loop) {
          player.loop = true;
        }

        player.playsInline = true;

        player.addEventListener('play', () => {
          this.pauseOtherVideos();
          this.setAttribute('playing', '');
          this.removeAttribute('suspended');
        });

        player.addEventListener('pause', () => {
          if (!player.seeking && player.paused) {
            this.removeAttribute('playing');
          }
        });

        player.addEventListener('ended', () => {
          if (this.loop && !this.paused) {
            player.currentTime = 0;
            player.play().catch(() => { });
          }
        });
      }

      return player;
    }
  }

  playerHandler(target, prop) {
    const host = this.getAttribute('host');

    if (host === 'youtube') {
      if (prop === 'play') {
        target.playVideo();
        if (!this.autoplay) {
          target.unMute();
        }
      } else {
        target.pauseVideo();
      }
      return;
    }

    if (host === 'vimeo') {
      if (prop === 'play') {
        target.play();
        if (!this.autoplay) {
          target.setMuted(false);
        }
      } else {
        target.pause();
      }
      return;
    }

    if (prop === 'play') {
      target.play().catch((error) => {
        if (error.name === 'NotAllowedError') {
          this.setAttribute('suspended', '');
          target.controls = true;

          const replacementImageSrc =
            target.previousElementSibling?.currentSrc;

          if (replacementImageSrc) {
            target.poster = replacementImageSrc;
          }
        }
      });
    } else {
      target[prop]();
    }
  }
}
customElements.define('video-media', VideoMedia);

/**
 * Register ProductJSON models and reveal [data-shopify-xr] buttons on AR devices (iOS Quick Look).
 * Must run early on page load — not only after the shopper plays a model.
 */
function bootstrapShopifyXRModels() {
  if (!window.ShopifyXR) return;

  document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
    try {
      const models = JSON.parse(modelJSON.textContent);
      if (models) window.ShopifyXR.addModels(models);
    } catch (_error) {
      /* Invalid or empty model JSON */
    }
    modelJSON.remove();
  });

  window.ShopifyXR.setupXRElements();
}

function loadShopifyXREarly() {
  if (!document.querySelector('[id^="ProductJSON-"], [data-shopify-xr]')) return;
  if (typeof Shopify === 'undefined' || !Shopify.loadFeatures) return;

  Shopify.loadFeatures([
    {
      name: 'shopify-xr',
      version: '1.0',
      onLoad: (errors) => {
        if (errors) return;
        if (!window.ShopifyXR) {
          document.addEventListener('shopify_xr_initialized', bootstrapShopifyXRModels, { once: true });
          return;
        }
        bootstrapShopifyXRModels();
      },
    },
  ]);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', loadShopifyXREarly, { once: true });
} else {
  loadShopifyXREarly();
}

document.addEventListener('shopify:section:load', () => {
  loadShopifyXREarly();
});

class ModelMedia extends CocoLazyMedia {
  constructor() {
    super();
    this._onModelClose = this.onCloseClick.bind(this);
    this.locked = false;
    this.raf = null;
    this.docGuard = null;
    this.hoverRAF = null;

    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnter = this.onPointerMove.bind(this);
    this.onPointerDown = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.onPointerEnter, { passive: true });
    this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.addEventListener('pointerleave', this.onPointerLeave);

    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.closeElement?.addEventListener('click', this._onModelClose);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerleave', this.onPointerLeave);

    if (this.locked) {
      this.toggleScrollLock(false);
      this.locked = false;
    }
    this.removeGuards();

    this.closeElement?.removeEventListener('click', this._onModelClose);
    super.disconnectedCallback();
  }

  isModelUIActive() {
    const modelViewer = this.querySelector('model-viewer');
    if (!modelViewer) return false;
    return !modelViewer.classList.contains('shopify-model-viewer-ui__disabled');
  }

  /** Product gallery needs pointer drags for Swiper — page scroll-lock fights that. */
  isInProductGallery() {
    return Boolean(this.closest('product-gallery'));
  }

  onPointerMove() {
    if (this.isInProductGallery()) return;
    if (this.raf) return;

    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      const shouldLock = this.isModelUIActive();

      if (shouldLock && !this.locked) {
        this.toggleScrollLock(true);
        this.locked = true;
      } else if (!shouldLock && this.locked) {
        this.toggleScrollLock(false);
        this.locked = false;
      }
    });
  }

  onPointerLeave() {
    if (this.locked) {
      this.toggleScrollLock(false);
      this.locked = false;
    }
  }

  toggleScrollLock(status) {
    if (status) {
      lockScroll(this);
      this.addGuards();
    } else {
      unlockScroll();
      this.removeGuards();
    }
  }

  addGuards() {
    if (this.docGuard) return;

    this.docGuard = (event) => {
      if (
        !this.contains(event.target) ||
        event.type === 'pointerup' ||
        event.type === 'pointercancel'
      ) {
        if (this.locked) {
          this.toggleScrollLock(false);
          this.locked = false;
        }
      }
    };

    document.addEventListener('pointermove', this.docGuard, true);
    document.addEventListener('pointerup', this.docGuard, true);
    document.addEventListener('pointercancel', this.docGuard, true);
    window.addEventListener('blur', this.docGuard);
    document.addEventListener('visibilitychange', this.docGuard);

    const tick = () => {
      if (!this.locked) {
        this.hoverRAF = null;
        return;
      }

      if (!this.matches(':hover')) {
        this.toggleScrollLock(false);
        this.locked = false;
        this.removeGuards();
        this.hoverRAF = null;
        return;
      }

      this.hoverRAF = requestAnimationFrame(tick);
    };

    this.hoverRAF = requestAnimationFrame(tick);
  }

  removeGuards() {
    if (!this.docGuard) return;

    document.removeEventListener('pointermove', this.docGuard, true);
    document.removeEventListener('pointerup', this.docGuard, true);
    document.removeEventListener('pointercancel', this.docGuard, true);
    window.removeEventListener('blur', this.docGuard);
    document.removeEventListener('visibilitychange', this.docGuard);
    this.docGuard = null;

    if (this.hoverRAF) {
      cancelAnimationFrame(this.hoverRAF);
      this.hoverRAF = null;
    }
  }

  get closeElement() {
    return this.querySelector('[id^="coco-media-poster-close-"]');
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.safeModelViewerUI('pause');
  }

  /** Shopify.ModelViewerUI.pause can throw / reject when the viewer isn't fully ready. */
  safeModelViewerUI(prop) {
    const ui = this.modelViewerUI;
    if (!ui || typeof ui[prop] !== 'function') return false;
    try {
      const result = ui[prop]();
      // model-viewer-ui.js uses async animation targets; rejections surface as
      // "Uncaught (in promise) TypeError: t.pause is not a function".
      if (result != null && typeof result.then === 'function') {
        result.catch(() => {});
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  play() {
    const hasModelContent = Boolean(
      this.querySelector('template')?.content?.querySelector('model-viewer') ||
        this.querySelector('model-viewer')
    );
    if (!hasModelContent) {
      this.setPosterHidden(false);
      return;
    }
    this.setPosterHidden(true);
    if (!this.playing) {
      this.pauseOtherModels();
      this.player.play();
    }
  }

  insertMediaContent(container, content) {
    const posterButton = container.querySelector(
      '.deferred-poster, [id^="coco-media-poster-"]'
    );

    if (posterButton) {
      container.insertBefore(content, posterButton);
    } else {
      container.appendChild(content);
    }
  }

  playerTarget() {
    return new Promise((resolve) => {
      const templateElement = this.querySelector('template');
      const existingViewer = this.querySelector('model-viewer');
      const container =
        this.querySelector('.deferred-media-model-stage') ||
        this.querySelector('[data-media-card]') ||
        this;

      if (templateElement && !existingViewer) {
        const content = templateElement.content.cloneNode(true);
        this.insertMediaContent(container, content);
      }

      templateElement?.remove();

      this.setAttribute('loaded', '');
      const modelViewer = this.querySelector('model-viewer');

      if (!modelViewer) {
        resolve(null);
        return;
      }

      const finish = () => {
        if (typeof Shopify !== 'undefined' && Shopify.loadFeatures) {
          Shopify.loadFeatures([
            {
              name: 'shopify-xr',
              version: '1.0',
              onLoad: this.setupShopifyXR.bind(this),
            },
            {
              name: 'model-viewer-ui',
              version: '1.0',
              onLoad: (errors) => {
                this.setupModelViewerUI(errors).finally(() => resolve(modelViewer));
              },
            },
          ]);
          return;
        }

        resolve(modelViewer);
      };

      if (window.customElements?.whenDefined) {
        customElements.whenDefined('model-viewer').then(finish).catch(finish);
      } else {
        finish();
      }
    });
  }

  playerHandler(target, prop) {
    if (this.safeModelViewerUI(prop)) return;
    // Native <model-viewer> has no play/pause — only Shopify.ModelViewerUI does.
    if (target?.tagName === 'MODEL-VIEWER') return;
    if (typeof target?.[prop] === 'function') target[prop]();
  }

  pause() {
    if (this.safeModelViewerUI('pause')) return;
    // Avoid Proxy → playerHandler calling non-existent model-viewer.pause().
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR(), { once: true });
      return;
    }

    bootstrapShopifyXRModels();
  }

  async setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector('model-viewer');
    if (!modelViewer || this.modelViewerUI) return;

    if (window.customElements?.whenDefined) {
      try {
        await customElements.whenDefined('model-viewer');
      } catch (_) {
        return;
      }
    }

    if (!window.Shopify?.ModelViewerUI) return;

    modelViewer.setAttribute('loaded', '');
    modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this));
    modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this));

    try {
      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    } catch (_) {
      this.modelViewerUI = null;
    }
  }

  modelViewerPlayed() {
    this.pauseOtherModels();
    this.setAttribute('playing', '');
    this.closeElement?.removeAttribute('hidden');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', { bubbles: true }));
  }

  modelViewerPaused() {
    this.removeAttribute('playing');
    this.closeElement?.setAttribute('hidden', '');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', { bubbles: true }));
  }

  /** Only one 3D model should animate at a time (same idea as video-media). */
  pauseOtherModels() {
    if (this._pausingOtherModels) return;

    this._pausingOtherModels = true;
    try {
      const scope =
        this.closest('product-info, product-gallery, [data-product-gallery], .shopify-section') ||
        document;

      scope.querySelectorAll('model-media').forEach((model) => {
        if (model === this) return;
        if (window.getComputedStyle(model).display === 'none') return;

        const shouldPause =
          model.hasAttribute('playing') ||
          Boolean(model.modelViewerUI) ||
          Boolean(model.querySelector('model-viewer'));

        if (!shouldPause) return;

        try {
          model.pause?.();
          model.removeAttribute('playing');
          model.closeElement?.setAttribute('hidden', '');
          model._activePlayback = false;
        } catch (_) {
          /* ignore */
        }
      });
    } finally {
      this._pausingOtherModels = false;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'playing') return;

    if (newValue === '') {
      this.pauseOtherModels();
      this.setPosterHidden(true);
    } else if (newValue === null && !this.autoplay && this.paused) {
      this.setPosterHidden(false);
    }
  }
}
customElements.define('model-media', ModelMedia);
class LazyMarquee extends HTMLElement {
  constructor() {
    super();
    this.intersectionObserver = null;
    this.initialized = false;
    this.disableOnMobile = this.dataset.marqueeMobile === 'false';
    this.mediaQuery = minWidthQuery(768);
    this.handleResize = this.handleResize.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
  }

  connectedCallback() {
    if (this.disableOnMobile && !this.mediaQuery.matches) return;

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = getObserver({ rootMargin: '150px 0px', threshold: 0 }, true, false);
      this.intersectionObserver.observe(this, () => {
        this.initMarquee();
        this.intersectionObserver?.unobserve(this);
      });
    } else {
      this.initMarquee();
    }

    if (this.disableOnMobile) {
      this.mediaQuery.addEventListener('change', this.handleResize);
    }

    document.addEventListener('shopify:section:load', this.handleSectionLoad);
  }

  disconnectedCallback() {
    if (this.disableOnMobile) {
      this.mediaQuery.removeEventListener('change', this.handleResize);
    }
    this.intersectionObserver?.unobserve(this);
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    this.destroyMarquee();
  }

  handleResize(e) {
    if (e.matches) {
      if (!this.initialized) this.initMarquee();
    } else {
      if (this.initialized) this.destroyMarquee();
    }
  }

  handleSectionLoad() {
    this.destroyMarquee();
    this.initialized = false;
    this.initMarquee();
  }

  initMarquee() {
    if (this.initialized || this._initRaf || !this.firstElementChild) return;

    // Defer measure+clone to next frame so IO/resize never force layout mid-style flush
    this._initRaf = requestAnimationFrame(() => {
      this._initRaf = 0;
      this.commitMarquee();
    });
  }

  commitMarquee() {
    if (this.initialized || !this.firstElementChild) return;

    // READ — both widths before any DOM mutation
    const parentWidth = this.offsetWidth;
    const childWidth = this.firstElementChild.offsetWidth;
    if (!parentWidth || !childWidth) return;

    this.initialized = true;

    const requiredClones = Math.ceil(parentWidth / childWidth) + 10;
    const fragment = document.createDocumentFragment();
    const source = this.firstElementChild;

    for (let i = 0; i < requiredClones; i++) {
      const clone = source.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('priority-hint', 'low');
      fragment.appendChild(clone);
    }

    // WRITE
    this.appendChild(fragment);
    this.classList.add('marquee-initialized');
  }

  destroyMarquee() {
    if (this._initRaf) {
      cancelAnimationFrame(this._initRaf);
      this._initRaf = 0;
    }
    if (!this.initialized) return;
    while (this.children.length > 1) {
      this.removeChild(this.lastElementChild);
    }
    this.classList.remove('marquee-initialized');
    this.initialized = false;
  }
}
customElements.define('lazy-marquee', LazyMarquee);

class TabComponent extends HTMLElement {
  constructor() {
    super();
    this.sliders = new Map();
    this.mediaQuery = window.matchMedia('(min-width: 768px)');
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleResize = debounce(this.handleResize.bind(this), 150);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this._designModeBound = false;
  }

  get isShopifyDesignMode() {
    return typeof Shopify !== 'undefined' && Shopify.designMode;
  }

  get isMobile() {
    return !this.mediaQuery.matches;
  }

  get showAllOnMobile() {
    return this.hasAttribute('data-show-all-mobile');
  }
  get tabsDesign() {
    return this.getAttribute('data-tabs-design');
  }

  bindDesignModeEvents() {
    if (!this.isShopifyDesignMode || this._designModeBound) return;

    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this._designModeBound = true;
  }

  unbindDesignModeEvents() {
    if (!this._designModeBound) return;

    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this._designModeBound = false;
  }

  onBlockSelect(event) {
    const blockEl = event.target.closest('[data-tab-content], [data-tab-item]');
    if (!blockEl || !this.contains(blockEl)) return;

    const blockIndex = blockEl.dataset.index;
    if (blockIndex == null || blockIndex === '') return;

    this.showTab(blockIndex);
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-tab-item]');
    this.tabsContent = this.querySelectorAll('[data-tab-content]');
    if (!this.tabsContent.length) return;

    this.tabsArray = Array.from(this.tabs);
    this.collectSliders();
    this.mediaQuery.addEventListener('change', this.handleResize);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
      tab.addEventListener('keydown', this.handleTabKeydown);
    });

    if (this.showAllOnMobile && this.isMobile && this.tabsDesign == 'full-width') {
      this.showAllTabs();
    } else if (this.tabs.length) {
      this.showTab(this.getActiveIndex());
    } else {
      this.initVisibleSliders();
    }

    this.bindDesignModeEvents();
  }

  disconnectedCallback() {
    this.unbindDesignModeEvents();
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.handleTabClick);
      tab.removeEventListener('keydown', this.handleTabKeydown);
    });
    this.mediaQuery.removeEventListener('change', this.handleResize);
    this.sliders.forEach((slot) => {
      slot.instance = destroySlider(slot.instance);
    });
    this.sliders.clear();
  }

  collectSliders() {
    this.tabsContent.forEach((content) => this.registerSliderForContent(content));
  }

  registerSliderForContent(content) {
    const index = content?.dataset?.index;
    if (index == null || this.sliders.has(index)) return;

    const element = content.querySelector('[data-slider]');
    if (!element) return;

    let params = {};
    try {
      params = element.dataset.slider ? JSON.parse(element.dataset.slider) : {};
    } catch (_error) {
      params = {};
    }

    this.sliders.set(index, { element, params, instance: null });
  }

  hydrateTabContent(content) {
    if (!content) return false;
    if (content.querySelector('.products-list, .collection-tabs-products')) return false;

    const template =
      content.querySelector(':scope > template') ||
      content.querySelector('details > template');
    if (!template) return false;

    const node = template.content.cloneNode(true).firstElementChild;
    if (!node) return false;

    template.replaceWith(node);
    this.registerSliderForContent(content);
    return true;
  }

  syncViewAll(index) {
    const panel = this.querySelector(`[data-tab-content][data-index="${index}"]`);
    const tab = this.querySelector(`[data-tab-item][data-index="${index}"]`);

    const link = this.querySelector('[data-collection-tabs-view-all]');
    if (link) {
      const url = panel?.dataset?.collectionUrl;
      const showViewAll = tab?.dataset?.allButton === 'true';

      link.closest('[data-tabs-view-all]')?.classList.toggle('hidden', !showViewAll);
      if (url) link.href = url;
    }

    // Predictive search: keep View all href in sync with the active resource type.
    const searchUrl = tab?.dataset?.searchViewAllUrl || panel?.dataset?.searchViewAllUrl;
    if (!searchUrl) return;

    const searchScope = this.closest('[data-coco-search-groups]') || this;
    searchScope.querySelectorAll('[data-coco-search-view-all]').forEach((searchLink) => {
      searchLink.href = searchUrl;
    });
  }

  getActiveIndex() {
    const active = Array.from(this.tabs).find((tab) => tab.classList.contains('active'));
    return (active ?? this.tabs[0])?.dataset.index ?? null;
  }

  showAllTabs() {
    this.tabsContent.forEach((content) => {
      if (content.dataset.sectionExecute == 'collection-tabs') {
        const elementDetails = content.querySelector('details');
        if (content.hasAttribute('hidden')) {
          elementDetails.removeAttribute('open');
        } else {
          elementDetails.setAttribute('open', '');
        }
      }
      content.removeAttribute('hidden');
      content.setAttribute('aria-hidden', 'false');
      content.setAttribute('tabindex', '0');
      this.hydrateTabContent(content);
      this.initSliderForIndex(content.dataset.index);
    });
  }

  initVisibleSliders() {
    this.tabsContent.forEach((content) => {
      if (content.dataset.sectionExecute == 'collection-tabs' && this.tabsDesign == 'full-width') {
        const elementDetails = content.querySelector('details');
        if (elementDetails) {
          elementDetails.setAttribute('open', '');
        }
      }
      if (content.hasAttribute('hidden')) return;
      this.hydrateTabContent(content);
      this.initSliderForIndex(content.dataset.index);
    });
  }

  showTab(index) {
    if (index == null) return;

    if (this.showAllOnMobile && this.isMobile && this.tabsDesign == 'full-width') {
      this.showAllTabs();
      return;
    }

    setTablistRovingState(this.tabsArray, (tab) => tab.dataset.index === index, {
      activeClass: 'active',
    });

    this.tabsContent.forEach((content) => {
      if (content.dataset.sectionExecute == 'collection-tabs') {
        const elementDetails = content.querySelector('details');
        if (elementDetails) {
          elementDetails.setAttribute('open', '');
        }
      }

      const isActive = content.dataset.index === index;
      if (isActive) {
        content.removeAttribute('hidden');
        content.setAttribute('aria-hidden', 'false');
        content.setAttribute('tabindex', '0');
        this.hydrateTabContent(content);
      } else {
        content.setAttribute('hidden', '');
        content.setAttribute('aria-hidden', 'true');
        content.setAttribute('tabindex', '-1');
      }
    });

    this.initSliderForIndex(index);
    this.syncViewAll(index);
  }

  initSliderForIndex(index) {
    const slot = this.sliders.get(index);
    if (!slot) return;

    if (slot.instance) {
      slot.instance.update?.();
      return;
    }

    void createSlider(slot.element, {
      ...slot.params,
      bypassLcpGate: true,
      onReady: (instance) => {
        slot.instance = instance;
      },
    }).then((instance) => {
      if (instance) slot.instance = instance;
    });
  }

  handleTabClick(event) {
    event.preventDefault();
    const index = event.currentTarget.dataset.index;
    if (index == null) return;
    this.showTab(index);
  }

  handleTabKeydown(event) {
    handleTablistKeydown(event, this.tabsArray, (tab) => {
      const idx = tab?.dataset?.index;
      if (idx != null) this.showTab(idx);
    });
  }

  handleResize() {
    this.sliders.forEach((slot) => {
      if (slot.instance) slot.instance = destroySlider(slot.instance);
    });

    if (this.showAllOnMobile && this.isMobile && this.tabsDesign == 'full-width') {
      this.showAllTabs();
      return;
    }

    if (this.tabs.length) {
      this.showTab(this.getActiveIndex());
      return;
    }

    this.initVisibleSliders();
  }
}
customElements.define('tab-component', TabComponent);

if (!customElements.get('recently-viewed')) {
  class RecentlyViewed extends HTMLElement {
    static MAX_ITEMS = 6;

    constructor() {
      super();

      this._observer = null;
      this._loaded = false;

      this.loadRecentlyViewed = this.loadRecentlyViewed.bind(this);
    }

    connectedCallback() {
      if (!RecentlyViewed.hasLocalStorage()) {
        this.hideSection();
        return;
      }

      // Record current product
      if (this.dataset.productId) {
        RecentlyViewed.record(this.dataset.productId);
      }

      // Show section only when there are 2+ items
      if (!this.hasRecentlyViewedProducts()) {
        this.hideSection();

        this.dispatchEvent(
          new CustomEvent('is-empty', {
            bubbles: true,
          }),
        );

        return;
      }

      if (this._loaded || !this.dataset.url) return;

      if (window.Shopify?.designMode) {
        this.loadRecentlyViewed();
        return;
      }

      if ('IntersectionObserver' in window) {
        this._observer = getObserver(
          { rootMargin: '600px 0px', threshold: 0 },
          true,
        );

        this._observer.observe(this, this.loadRecentlyViewed);
      } else {
        this.loadRecentlyViewed();
      }
    }

    disconnectedCallback() {
      this._observer?.unobserve(this);
      this._observer = null;
    }

    /**
     * Show only when there are 2 or more
     * recently viewed products in LocalStorage.
     */
    hasRecentlyViewedProducts() {
      return RecentlyViewed.readItems().length >= 2;
    }

    /**
     * Hide complete Shopify section.
     */
    hideSection() {
      this.closest('.shopify-section')?.classList.add('hidden');
    }

    /**
     * Show complete Shopify section.
     */
    showSection() {
      this.closest('.shopify-section')?.classList.remove('hidden');
    }

    static hasLocalStorage() {
      if (window.theme?.config?.hasLocalStorage === false) {
        return false;
      }

      if (window.theme?.config?.hasLocalStorage === true) {
        return true;
      }

      try {
        const key = '__storage_test__';

        window.localStorage.setItem(key, key);
        window.localStorage.removeItem(key);

        return true;
      } catch {
        return false;
      }
    }

    static getStorageKey() {
      const themeName =
        window.theme?.settings?.themeName || 'coco';

      return `${themeName}:recently-viewed`;
    }

    static readItems() {
      try {
        return JSON.parse(
          window.localStorage.getItem(
            RecentlyViewed.getStorageKey(),
          ) || '[]',
        )
          .map((item) => parseInt(item, 10))
          .filter((item) => !Number.isNaN(item));
      } catch {
        return [];
      }
    }

    static writeItems(items) {
      window.localStorage.setItem(
        RecentlyViewed.getStorageKey(),
        JSON.stringify(
          items.slice(0, RecentlyViewed.MAX_ITEMS),
        ),
      );
    }

    /**
     * Add product to recently viewed.
     * Newest product comes first.
     */
    static record(productId) {
      if (!RecentlyViewed.hasLocalStorage() || !productId) {
        return;
      }

      const id = parseInt(productId, 10);

      if (Number.isNaN(id)) {
        return;
      }

      const items = RecentlyViewed.readItems().filter(
        (item) => item !== id,
      );

      items.unshift(id);

      RecentlyViewed.writeItems(items);
    }

    getQueryString() {
      const excludeId = this.dataset.productId
        ? parseInt(this.dataset.productId, 10)
        : null;

      const limit = Math.min(
        parseInt(
          this.dataset.limit || RecentlyViewed.MAX_ITEMS,
          10,
        ) || RecentlyViewed.MAX_ITEMS,
        RecentlyViewed.MAX_ITEMS,
      );

      return RecentlyViewed.readItems()
        .filter((item) => item !== excludeId)
        .slice(0, limit)
        .map((item) => `id:${item}`)
        .join(' OR ');
    }

    loadRecentlyViewed() {
      if (this._loaded || !this.dataset.url) {
        return;
      }

      const query = this.getQueryString();

      /**
       * No products available after excluding
       * the current product.
       */
      if (!query) {
        this.hideSection();

        this.dispatchEvent(
          new CustomEvent('is-empty', {
            bubbles: true,
          }),
        );

        return;
      }

      this._loaded = true;

      fetch(
        `${this.dataset.url}${encodeURIComponent(query)}`,
      )
        .then((response) => response.text())
        .then((responseText) => {
          const sectionInnerHTML = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .querySelector('[id^="shopify-section-"]');

          if (!sectionInnerHTML) {
            this.hideSection();
            this._loaded = false;
            return;
          }

          const recentlyViewed =
            sectionInnerHTML.querySelector(
              'recently-viewed',
            );

          const sourceWrapper =
            recentlyViewed?.querySelector(
              '[data-swiper-wrapper]',
            );

          const targetWrapper =
            this.querySelector(
              '[data-swiper-wrapper]',
            );

          const hasProducts = Boolean(
            sourceWrapper?.querySelector(
              '[data-swiper-slide], .swiper-slide, .product-card',
            ),
          );

          /**
           * Products found inside swiper wrapper.
           */
          if (
            hasProducts &&
            sourceWrapper &&
            targetWrapper
          ) {
            targetWrapper.innerHTML =
              sourceWrapper.innerHTML;

            this.showSection();

            this.dispatchEvent(
              new CustomEvent(
                'recentlyViewed:loaded',
                {
                  bubbles: true,
                },
              ),
            );

            return;
          }

          /**
           * Products found without swiper wrapper.
           */
          if (
            recentlyViewed?.innerHTML.trim().length &&
            hasProducts
          ) {
            this.innerHTML =
              recentlyViewed.innerHTML;

            this.showSection();

            this.dispatchEvent(
              new CustomEvent(
                'recentlyViewed:loaded',
                {
                  bubbles: true,
                },
              ),
            );

            return;
          }

          /**
           * LocalStorage has IDs but Shopify
           * returned no valid products.
           */
          this.hideSection();

          this.dispatchEvent(
            new CustomEvent('is-empty', {
              bubbles: true,
            }),
          );
        })
        .catch((error) => {
          this._loaded = false;

          this.hideSection();

          console.error(error);
        });
    }
  }

  customElements.define(
    'recently-viewed',
    RecentlyViewed,
  );

  /**
   * Record current product.
   */
  const currentProductId =
    document.querySelector(
      'product-info[data-product-id]',
    )?.dataset.productId;

  if (currentProductId) {
    RecentlyViewed.record(currentProductId);
  }
}

if (!customElements.get('swatch-expander')) {
  class SwatchExpander extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
      this.onClose = this.onClose.bind(this);
    }

    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-swatch-expander-toggle]');
      this.panel = this.querySelector('[data-swatch-expander-panel]');
      this.closeBtn = this.querySelector('[data-swatch-expander-close]');
      this.hiddenSwatches = [...this.querySelectorAll('[data-swatch].hidden, [data-swatch][hidden]')];

      if (!this.toggleBtn) return;
      if (!this.panel && this.hiddenSwatches.length === 0) return;

      this.toggleBtn.addEventListener('click', this.onToggle);
      this.closeBtn?.addEventListener('click', this.onClose);
    }

    disconnectedCallback() {
      this.toggleBtn?.removeEventListener('click', this.onToggle);
      this.closeBtn?.removeEventListener('click', this.onClose);
    }

    get isOpen() {
      if (this.panel) return !this.panel.hidden;
      return this.classList.contains('is-open');
    }

    setToggleVisible(visible) {
      this.toggleBtn?.classList.toggle('hidden', !visible);
      this.closeBtn?.classList.toggle('hidden', visible);
    }

    open() {
      if (this.isOpen) return;

      if (this.panel) {
        this.panel.hidden = false;
      } else {
        this.hiddenSwatches.forEach((swatch) => {
          swatch.classList.remove('hidden');
          swatch.hidden = false;
        });
      }

      this.classList.add('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.setToggleVisible(false);
      this.closeBtn?.focus();
    }

    close({ restoreFocus = false } = {}) {
      if (!this.isOpen) return;

      if (this.panel) {
        this.panel.hidden = true;
      } else {
        this.hiddenSwatches.forEach((swatch) => {
          swatch.classList.add('hidden');
          swatch.hidden = true;
        });
      }

      this.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.setToggleVisible(true);
      if (restoreFocus) this.toggleBtn?.focus();
    }

    onToggle() {
      this.isOpen ? this.close() : this.open();
    }

    onClose(event) {
      event?.preventDefault();
      this.close({ restoreFocus: true });
    }
  }

  customElements.define('swatch-expander', SwatchExpander);

}
class HeaderUtilities extends HTMLElement {
  async connectedCallback() {
    const account = this.querySelector('shopify-account');
    if (!account) return;

    await customElements.whenDefined('shopify-account');
    await this.waitForShadow(account);
    this.injectAnimation(account);
    this.watchDialog(account);
  }

  waitForShadow(account, attempts = 30) {
    return new Promise((resolve) => {
      const check = (remaining) => {
        if (account.shadowRoot) {
          resolve(account.shadowRoot);
          return;
        }

        if (remaining <= 0) {
          resolve(null);
          return;
        }

        requestAnimationFrame(() => check(remaining - 1));
      };

      check(attempts);
    });
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;
    if (!shadow || shadow.querySelector("#custom-account-styles"))
      return;
    const style = document.createElement("style");
    style.id = "custom-account-styles";
    style.textContent = `
      *:focus {
        outline: 0;
        box-shadow: none;
      }
      *:focus-visible {
        outline: 2px solid rgb(var(--textColor));
        outline-offset: 1px;
      }
      @keyframes dialogScaleIn {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes dialogScaleOut {
        0% {
          transform: scale(1);
          opacity: 1;
        }

        100% {
          transform: scale(0.8);
          opacity: 0;
        }
      }
      @keyframes dialogBackdropIn {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
      @keyframes dialogBackdropOut {
        0% {
          opacity: 1;
        }

        100% {
          opacity: 0;
        }
      }
      @media (min-width: 751px) {
        dialog .account {
          height: fit-content;
          background: var(--shopify-account-color-background);
          border-radius: inherit;
        }
        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        } 
      }
      @media (max-width: 750px) {
        dialog .account__content {
          padding-bottom: 2.4rem;
        }
      }
    
  `,
      shadow.prepend(style)
  }
  watchDialog(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    let releaseModal = null;

    const activateAccountModal = (dialog) => {
      const previouslyFocused = document.activeElement;

      lockScroll(account);
      document.documentElement.classList.add('account-dialog-open');

      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }

      const onKeydown = (event) => {
        if (event.key !== 'Tab') return;

        const focusables = getVisibleFocusable(dialog);
        if (!focusables.length) {
          event.preventDefault();
          dialog.focus?.({ preventScroll: true });
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = shadow.activeElement || document.activeElement;
        const inDialog = Boolean(active && dialog.contains(active));

        if (event.shiftKey && (active === first || !inDialog)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        } else if (!event.shiftKey && (active === last || !inDialog)) {
          event.preventDefault();
          first.focus({ preventScroll: true });
        }
      };

      const onFocusIn = (event) => {
        if (event.composedPath().includes(dialog)) return;
        const focusables = getVisibleFocusable(dialog);
        (focusables[0] || dialog)?.focus?.({ preventScroll: true });
      };

      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('focusin', onFocusIn);

      requestAnimationFrame(() => {
        const focusables = getVisibleFocusable(dialog);
        (focusables[0] || dialog)?.focus?.({ preventScroll: true });
      });

      let released = false;
      return (focusOverride) => {
        if (released) return;
        released = true;
        document.removeEventListener('keydown', onKeydown, true);
        document.removeEventListener('focusin', onFocusIn);
        document.documentElement.classList.remove('account-dialog-open');
        unlockScroll();
        if (focusOverride === false) return;
        const target =
          focusOverride ||
          (previouslyFocused?.isConnected ? previouslyFocused : account);
        target?.focus?.({ preventScroll: true });
      };
    };

    const bindDialog = (dialog) => {
      if (!dialog || dialog.dataset.utilitiesBound === 'true') return;

      dialog.dataset.utilitiesBound = 'true';

      const sync = () => {
        const isOpen = dialog.hasAttribute('open');

        if (isOpen) {
          releaseModal?.();
          releaseModal = activateAccountModal(dialog);
          return;
        }

        if (releaseModal) {
          releaseModal(account);
          releaseModal = null;
        }
      };

      dialog.addEventListener('close', () => {
        if (!releaseModal) return;
        releaseModal(account);
        releaseModal = null;
      });

      sync();
      new MutationObserver(sync).observe(dialog, {
        attributes: true,
        attributeFilter: ['open'],
      });
    };

    bindDialog(shadow.querySelector('dialog'));

    new MutationObserver(() => {
      bindDialog(shadow.querySelector('dialog'));
    }).observe(shadow, {
      childList: true,
      subtree: true,
    });
  }
}
customElements.define("header-utilities", HeaderUtilities);

(() => {
  const focusSkipTarget = (target, { preferFirstFocusable = false, preferFirstLink = false } = {}) => {
    if (!target) return;

    let focusEl = target;

    if (preferFirstLink) {
      const links = Array.from(target.querySelectorAll('a[href]')).filter((el) => {
        if (el.closest('[hidden], [aria-hidden="true"]')) return false;
        if (el.classList.contains('skip-to-content-link') || el.classList.contains('visually-hidden')) {
          return false;
        }
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0;
      });
      if (links.length) focusEl = links[0];
    } else if (preferFirstFocusable) {
      const focusables =
        typeof window.theme?.a11y?.getVisibleFocusable === 'function'
          ? window.theme.a11y.getVisibleFocusable(target)
          : Array.from(
              target.querySelectorAll(
                window.theme?.a11y?.FOCUSABLE_SELECTOR ||
                  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])',
              ),
            );
      if (focusables.length) focusEl = focusables[0];
    }

    if (focusEl === target && !target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }

    focusEl.focus({ preventScroll: true });

    const top = target.getBoundingClientRect().top + window.scrollY;
    if (window.lenis?.scrollTo) {
      window.lenis.scrollTo(top);
    } else {
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  // Delegation so dynamically loaded quick-view markup also works.
  document.addEventListener('click', (event) => {
    const skipLink = event.target.closest('[data-skip-link]');
    if (!skipLink) return;

    const href = skipLink.getAttribute('href') || '';
    if (!href.startsWith('#') || href.length < 2) return;

    let target;
    try {
      target = document.querySelector(href);
    } catch (_) {
      return;
    }
    if (!target) return;

    event.preventDefault();

    const isProductSkip =
      skipLink.classList.contains('skip-to-product-link') ||
      Boolean(target.classList?.contains('product-info-wrapper')) ||
      Boolean(target.id?.startsWith('ProductInfo-'));

    // Skip to content → first visible link in main; product skip → first focusable in info.
    focusSkipTarget(target, {
      preferFirstLink: !isProductSkip && (target.id === 'MainContent' || href === '#MainContent'),
      preferFirstFocusable: isProductSkip,
    });
  });
})();

class CountryProvince extends HTMLElement {
  constructor() {
    super();

    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    if (!this.countryElement || !this.provinceElement) return;

    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

    if (this.dataset.country) {
      const matchIndex = Array.from(this.countryElement.options).findIndex(
        (option) => option.textContent === this.dataset.country || option.value === this.dataset.country
      );
      this.countryElement.selectedIndex = Math.max(0, matchIndex);
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement?.options?.[this.countryElement.selectedIndex];
    const wrapper = this.provinceElement?.closest('[data-province-wrapper]');
    if (!option || !wrapper) return;

    let provinces = [];
    try {
      const raw = option.dataset?.provinces;
      provinces = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(provinces)) provinces = [];
    } catch (error) {
      provinces = [];
    }

    wrapper.hidden = provinces.length === 0;
    if (provinces.length === 0) return;

    this.provinceElement.innerHTML = '';
    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvince);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.submitText = this.querySelectorAll('[data-submit-text]');
    this.submitIcon = this.querySelectorAll('[data-submit-icon]');
    this.resultsElement = this.querySelector('[data-shipping-calculator-results]');
    this.loadingSpinner = this.querySelector('[data-loading-spinner]');

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton._button?.contentTimeline?.reverse();
    this.submitButton._button?.borderTimeline?.reverse();
    this.loadingSpinner?.classList.remove('hidden');
    this.submitText?.forEach(text => text.classList.add('hidden'));
    this.submitIcon?.forEach(icon => icon.classList.add('hidden'));
    const body = JSON.stringify({
      shipping_address: { zip, country, province }
    });
    let sectionUrl = `${window.Coco.routes.cart}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...cocoFetchInit('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        }
        else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.loadingSpinner?.classList.add('hidden');
        this.submitText?.forEach(text => text.classList.remove('hidden'));
        this.submitIcon?.forEach(icon => icon.classList.remove('hidden'));
        if (this.resultsElement) this.resultsElement.hidden = false;
        this.submitButton.removeAttribute('aria-busy');
      });

  }

  formatError(errors) {
    const strings = getShippingCalculatorStrings();
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
    <div class="form-message form-message-error">
      <p>${strings.error}</p>
      <ul class="form-error" role="list">${shippingRatesList.join('')}</ul>
    </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const strings = getShippingCalculatorStrings();
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="form-message form-message-${shippingRates.length === 0 ? 'error' : 'success'}">
        <p>${shippingRates.length === 0 ? strings.notFound : shippingRates.length === 1 ? strings.oneResult : strings.multipleResults}</p>
        ${shippingRatesList === '' ? '' : `<ul class="form-error">${shippingRatesList.join('')}</ul>`}
      </div>
    `;

  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.submitIcon = this.submitButton?.querySelectorAll('[data-submit-icon], [data-cart-discount-apply-icon]');
    this.submitText = this.submitButton?.querySelectorAll('[data-submit-text], [data-cart-discount-apply]');
    this.fancyButtonContent = this.submitButton?.querySelector('[data-button-inner]')?.querySelectorAll(':scope > :not([data-loading-spinner])');
    this.messageEl = this.querySelector('[data-cart-discount-error]') || this.querySelector('[data-form-message]');
    this.couponEl = this.querySelector('input[name="discount"]');
    this.loadingSpinner = this.submitButton?.querySelector('[data-loading-spinner]');

    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  get cartAddonDrawer() {
    return this.closest('[data-cart-discount-modal]');
  }

  getSectionsToBundle() {
    const sections = [];
    const drawerCartItems = document.querySelector('coco-cart-drawer coco-cart-list');

    if (drawerCartItems) {
      [
        drawerCartItems.dataset.sectionId,
        drawerCartItems.dataset.id,
        drawerCartItems.dataset.sectionKey,
      ]
        .filter(Boolean)
        .forEach((id) => sections.push(id));
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push('cart-icon-bubble');
    }

    const pageCartItems = document.querySelector('[data-cart-container] coco-cart-list, [data-cart-container] #coco-cart-items');
    const pageSectionId = pageCartItems?.dataset?.id;
    if (pageSectionId) sections.push(pageSectionId);

    return [...new Set(sections)];
  }

  getSectionsUrl() {
    if (document.querySelector('[data-cart-container]')) {
      return window.location.pathname;
    }

    return window.Coco.routes?.root || '/';
  }

  async getDiscounts() {
    try {
      const response = await fetch(window.Coco.routes.cart, cocoFetchInit());
      const cart = await response.json();

      return (cart.discount_codes || [])
        .filter((discount) => discount.applicable)
        .map((discount) => discount.code);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  setLoadingState(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton._button?.contentTimeline?.reverse();
      this.submitButton._button?.borderTimeline?.reverse();
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.setAttribute('aria-busy', 'true');
      this.loadingSpinner?.classList.remove('hidden');

      if (this.fancyButtonContent?.length) {
        this.fancyButtonContent.forEach((layer) => layer.classList.add('hidden'));
      } else {
        this.submitIcon?.forEach((icon) => icon.classList.add('hidden'));
        this.submitText?.forEach((text) => text.classList.add('hidden'));
      }

      return;
    }

    this.submitButton.removeAttribute('aria-disabled');
    this.submitButton.removeAttribute('aria-busy');
    this.loadingSpinner?.classList.add('hidden');

    if (this.fancyButtonContent?.length) {
      this.fancyButtonContent.forEach((layer) => layer.classList.remove('hidden'));
    } else {
      this.submitIcon?.forEach((icon) => icon.classList.remove('hidden'));
      this.submitText?.forEach((text) => text.classList.remove('hidden'));
    }
  }

  closeAddonDrawer() {
    this.cartAddonDrawer?.querySelector('[is="close-drawer"]')?.click();
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl?.value?.trim();
    if (!newDiscountCode) return;

    this.setLoadingState(true);

    try {
      const discounts = await this.getDiscounts();

      if (discounts.includes(newDiscountCode)) {
        this.displayFormErrors(window.Coco.cartStrings?.duplicateDiscountError);
        return;
      }

      discounts.push(newDiscountCode);

      const sectionsToBundle = this.getSectionsToBundle();
      const config = cocoFetchInit('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('sections', sectionsToBundle.join(','));
      formData.append('sections_url', this.getSectionsUrl());
      formData.append('discount', discounts.join(','));
      config.body = formData;

      const response = await fetch(window.Coco.routes.cartUpdate, config);
      const parsedState = await response.json();
      const invalidDiscount = parsedState.discount_codes?.find(
        (discount) => discount.code === newDiscountCode && discount.applicable === false,
      );

      if (invalidDiscount) {
        this.couponEl.value = '';
        this.displayFormErrors(window.Coco.cartStrings?.applyDiscountError);
        return;
      }

      this.couponEl.value = '';
      this.closeAddonDrawer();

      const cartJson = await (
        await fetch(window.Coco.routes.cart, cocoFetchInit())
      ).json();
      cartJson.sections = parsedState.sections;

      emitCocoEvent(COCO_EVENTS.cartUpdated, {
        source: 'cart-discount',
        cart: cartJson,
      });
    } catch (error) {
      console.error(error);
      this.displayFormErrors(window.Coco.cartStrings?.error);
    } finally {
      this.setLoadingState(false);
    }
  }

  displayFormErrors(errorMessage = false) {
    if (!this.messageEl) {
      if (errorMessage) alert(errorMessage);
      return;
    }

    if (errorMessage) {
      this.messageEl.textContent = errorMessage;
      this.messageEl.hidden = false;
      return;
    }

    this.messageEl.textContent = '';
    this.messageEl.hidden = true;
  }
}
customElements.define('cart-discount', CartDiscount, { extends: 'form' });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.loadingSpinner = this.querySelector('[data-loading-spinner]');
    this.submitText = this.querySelectorAll('[data-submit-text]');
    this.submitIcon = this.querySelectorAll('[data-submit-icon]');
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  getDiscountCodeToRemove() {
    const ariaLabel = this.getAttribute('aria-label') || '';
    const prefix = 'Remove ';

    return ariaLabel.startsWith(prefix) ? ariaLabel.slice(prefix.length).trim() : '';
  }

  getSectionsToBundle() {
    const sections = [];
    const drawerCartItems = document.querySelector('coco-cart-drawer coco-cart-list');

    if (drawerCartItems) {
      [
        drawerCartItems.dataset.sectionId,
        drawerCartItems.dataset.id,
        drawerCartItems.dataset.sectionKey,
      ]
        .filter(Boolean)
        .forEach((id) => sections.push(id));
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push('cart-icon-bubble');
    }

    const pageCartItems = document.querySelector('[data-cart-container] coco-cart-list, [data-cart-container] #coco-cart-items');
    const pageSectionId = pageCartItems?.dataset?.id;
    if (pageSectionId) sections.push(pageSectionId);

    return [...new Set(sections)];
  }

  getSectionsUrl() {
    if (document.querySelector('[data-cart-container]')) {
      return window.location.pathname;
    }

    return window.Coco.routes?.root || '/';
  }

  async getApplicableDiscountCodes() {
    try {
      const response = await fetch(window.Coco.routes.cart, cocoFetchInit());
      const cart = await response.json();

      return (cart.discount_codes || [])
        .filter((discount) => discount.applicable)
        .map((discount) => discount.code);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  setLoadingState(isLoading) {
    this.toggleAttribute('aria-disabled', isLoading);

    this.querySelectorAll(':scope > *:not([data-loading-spinner])').forEach((element) => {
      element.classList.toggle('hidden', isLoading);
    });

    this.loadingSpinner?.classList.toggle('hidden', !isLoading);
    this.submitText?.forEach(text => text.classList.toggle('hidden', isLoading));
    this.submitIcon?.forEach(icon => icon.classList.toggle('hidden', isLoading));
  }

  async handleClick(event) {
    event.preventDefault();

    if (this.getAttribute('aria-disabled') === 'true') return;

    const codeToRemove = this.getDiscountCodeToRemove();
    if (!codeToRemove) return;

    const discounts = (await this.getApplicableDiscountCodes()).filter(
      (code) => code !== codeToRemove,
    );

    const sectionsToBundle = this.getSectionsToBundle();
    const config = cocoFetchInit('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle.join(','));
    formData.append('sections_url', this.getSectionsUrl());
    formData.append('discount', discounts.join(','));
    config.body = formData;

    this.setLoadingState(true);

    fetch(window.Coco.routes.cartUpdate, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (
          await fetch(window.Coco.routes.cart, cocoFetchInit())
        ).json();
        cartJson.sections = parsedState.sections;

        emitCocoEvent(COCO_EVENTS.cartUpdated, {
          source: 'cart-discount-remove',
          cart: cartJson,
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.setLoadingState(false);
      });
  }
}
customElements.define('cart-discount-remove', CartDiscountRemove, { extends: 'button' });

class IconsCarousel extends HTMLDivElement {
  constructor() {
    super();
    this.scrollDuration = 90;
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.scrollFrame = null;
  }

  connectedCallback() {
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.scrollFrame);
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasOverflowCSS =
      /^(scroll|auto|hidden)$/.test(style.overflow) || /^(scroll|auto|hidden)$/.test(style.overflowX);

    const hasContentToScroll = this.scrollWidth > this.clientWidth;

    return hasOverflowCSS && hasContentToScroll;
  }

  get isRTL() {
    return getComputedStyle(this).direction === 'rtl' || this.getAttribute('dir') === 'rtl';
  }

  animateScroll(targetLeft) {
    cancelAnimationFrame(this.scrollFrame);

    const startLeft = this.scrollLeft;
    const distance = targetLeft - startLeft;

    if (distance === 0) return;

    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const timeElapsed = timestamp - startTime;

      const percent = Math.min(timeElapsed / this.scrollDuration, 1);
      const ease = percent * (2 - percent); // Smooth ease-out

      this.scrollLeft = startLeft + distance * ease;

      if (timeElapsed < this.scrollDuration) {
        this.scrollFrame = requestAnimationFrame(step);
      }
    };

    this.scrollFrame = requestAnimationFrame(step);
  }

  onMouseEnter() {
    if (!this.isScrollable) return;

    const maxScroll = this.scrollWidth - this.clientWidth;
    const target = this.isRTL ? -maxScroll : maxScroll;

    this.animateScroll(target);
  }

  onMouseLeave() {
    if (!this.isScrollable) return;

    this.animateScroll(0);
  }
}
customElements.define('icons-carousel', IconsCarousel, { extends: 'div' });

class LoadingPage extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('page:loaded', () => {
      this.hide();
    });

    document.addEventListener('loading-page:show', () => {
      this.show();
    });

    document.addEventListener('loading-page:hide', () => {
      this.hide();
    });
  }

  show() {
    this.hidden = false;
    // this.style.opacity = 1;
    window.Motion.animate(this, { opacity: 1 }, { duration: 0.2 });
  }

  hide() {
    /*
    motion.animate(this, { opacity: 0 }, { duration: 0.3 }).finished.then(() => {
      this.hidden = true;
    });
    */
    window.Motion.animate(this, { opacity: 0 }, { duration: 1 }).finished.then(() => {
      this.hidden = true;
    });
    document.dispatchEvent(new CustomEvent('page:loader-finished'));
  }
}
customElements.define('loading-page', LoadingPage);

if (!customElements.get('spinning-text-rotate')) {
  class SpinningTextRotate extends HTMLElement {
    connectedCallback() {
      if (!this._onResize) {
        this._onResize = () => this.render();
        window.addEventListener('resize', this._onResize);
      }
      this.scheduleRender();
    }
    disconnectedCallback() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }
    scheduleRender(attempts = 0) {
      if (this.querySelector('[spining-text-character]')) {
        this.render();
        return;
      }
      if (attempts > 20) return;
      this._raf = requestAnimationFrame(() => this.scheduleRender(attempts + 1));
    }

    getSvgSize() {
      const svg = this.querySelector('.spinning-svg, svg');
      if (!svg) return 61;
      const attrW = parseFloat(svg.getAttribute('width'));
      const attrH = parseFloat(svg.getAttribute('height'));
      if (attrW > 0 && attrH > 0) return Math.max(attrW, attrH);
      const viewBox = (svg.getAttribute('viewBox') || '')
        .trim()
        .split(/[\s,]+/)
        .map(Number);
      if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
        return Math.max(viewBox[2], viewBox[3]);
      }
      const rect = svg.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return Math.max(rect.width, rect.height);
      return 61;
    }

    render() {
      const getFontSize = getComputedStyle(this).getPropertyValue('--spiningText');
      const resultFontSize = parseFloat(getFontSize.match(/[\d.]+(?=px)/)?.[0] || 0);
      const CONFIG = {
        fontSize: resultFontSize,
        slotFactor: 1.05,
        circleTextGap: 16,
        charGapReduce: 4,
      };
      const raw = this.dataset.string || '';
      const words = raw
        .trim()
        .replace(/\s*•\s*/g, ' ')
        .split(/\s+/)
        .filter(Boolean);
      const charString = words.length ? `•${words.join('•')}`.toUpperCase() : '';
      const chars = [...charString];
      const splitEl = this.querySelector('[spining-text-character]');
      if (!chars.length || !splitEl) return;
      const fontSize = CONFIG.fontSize;
      const svgSize = this.getSvgSize();
      const svgRadius = svgSize / 2;
      const minTextRadius = svgRadius + CONFIG.circleTextGap + fontSize * 0.55;
      const slot = fontSize * CONFIG.slotFactor;
      const textRadius = Math.max((chars.length * slot) / (2 * Math.PI), minTextRadius);
      const circ = 2 * Math.PI * textRadius;
      const baseArc = circ / chars.length;
      const isWordBreak = (char) => char === '•' || /\s/.test(char);
      const letterCount = chars.filter((char) => !isWordBreak(char)).length;
      const breakCount = chars.length - letterCount;
      const letterArc = baseArc - CONFIG.charGapReduce;
      const breakArc = breakCount
        ? (circ - letterCount * letterArc) / breakCount
        : baseArc;
      const size = (textRadius + fontSize * 0.55) * 2;
      this.style.setProperty('--text-radius', `${textRadius}px`);
      this.style.setProperty('--size', `${size}px`);
      this.style.setProperty('--font-size', `${fontSize}px`);
      let cursor = 0;
      splitEl.replaceChildren(
        ...chars.map((char) => {
          const wordBreak = isWordBreak(char);
          const arc = wordBreak ? breakArc : letterArc;
          const place = wordBreak ? cursor + (breakArc - letterArc) / 2 : cursor;
          const span = document.createElement('span');
          span.className = 'split-char';
          span.setAttribute('aria-hidden', 'true');
          span.style.setProperty('--char-rotate', `${(place / circ) * 360}deg`);
          span.textContent = char;
          cursor += arc;
          return span;
        })
      );
      requestAnimationFrame(() => this.fitSizeToContent());
    }
    fitSizeToContent() {
      const splitEl = this.querySelector('[spining-text-character]');
      if (!splitEl) return;
      const host = this.getBoundingClientRect();
      if (host.width <= 0 || host.height <= 0) return;
      const cx = host.left + host.width / 2;
      const cy = host.top + host.height / 2;
      let maxDist = 0;
      splitEl.querySelectorAll('.split-char').forEach((el) => {
        const r = el.getBoundingClientRect();
        [
          [r.left, r.top],
          [r.right, r.top],
          [r.left, r.bottom],
          [r.right, r.bottom],
        ].forEach(([x, y]) => {
          maxDist = Math.max(maxDist, Math.hypot(x - cx, y - cy));
        });
      });
      if (maxDist <= 0) return;
      const next = Math.ceil(maxDist * 2);
      this.style.setProperty('--size', `${next}px`);
      const sectionShopify = this.closest(".shopify-section");
      sectionShopify?.style.setProperty("--size", `${next}px`);
    }
  }
  customElements.define('spinning-text-rotate', SpinningTextRotate);
}
document.querySelectorAll('spinning-text-rotate').forEach((el) => {
  if (typeof el.scheduleRender === 'function') el.scheduleRender();
});

/*============================================================================
  Things that require DOM to be ready
==============================================================================*/
(function () {
  theme.DOMready = function (callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  };

  // Jab next section pe section-rounded ho → previous pe section-next-rounded (sab previous pe)
  theme.syncRoundedPreviousSections = function () {
    document.querySelectorAll('[data-rounded-prev]').forEach((el) => {
      const classes = (el.getAttribute('data-rounded-prev') || '').split(/\s+/).filter(Boolean);
      classes.forEach((cls) => el.classList.remove(cls));
      el.removeAttribute('data-rounded-prev');
    });

    document.querySelectorAll('.section-rounded').forEach((el) => {
      const prev = el.closest('.shopify-section')?.previousElementSibling;
      if (!prev?.classList.contains('shopify-section')) return;

      const root = [...prev.children].find(
        (child) =>
          child.nodeType === 1 &&
          !['STYLE', 'SCRIPT', 'LINK'].includes(child.tagName) &&
          !child.hasAttribute('data-section-divider')
      );
      if (!root) return;

      const added = [];
      ['section', 'relative', 'section-next-rounded'].forEach((cls) => {
        if (!root.classList.contains(cls)) {
          root.classList.add(cls);
          added.push(cls);
        }
      });

      if (added.length) {
        root.setAttribute('data-rounded-prev', added.join(' '));
      }
    });
  };

  theme.headerGroup = {
    rounded: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group .section'));
    },
    sections: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    },
    init: () => {
      let previousSection = null;
      const rounded = theme.headerGroup.rounded();
      const sections = theme.headerGroup.sections();
      sections.forEach((shopifySection, index) => {
        const section = shopifySection.querySelector('.section');
        if (section) {
          section.classList.remove('section-next-rounded');
          section.classList.remove('section-first-rounded');
          section.classList.remove('section-last-rounded');

          if (section.classList.contains('section-rounded')) {
            if (index === 0) {
              section.classList.add('section-first-rounded');
            }

            if (index === rounded.length - 1) {
              section.classList.add('section-last-rounded');
            }

            if (previousSection && previousSection.classList.contains('section-rounded')) {
              previousSection.classList.add('section-next-rounded');
            }
          }

          if (index === sections.length - 1) {
            const nextSection = document.querySelector('#MainContent .shopify-section:first-child .section');
            if (nextSection && nextSection.classList.contains('section-rounded')) {
              section.classList.add('section-next-rounded');
            }
          }

          previousSection = section;
        }
      });
    },
  };

  theme.DOMready(function () {
    theme.syncRoundedPreviousSections();
    theme.headerGroup.init();
    
  });

  document.addEventListener('shopify:section:load', function () {
   theme.syncRoundedPreviousSections();
   theme.headerGroup.init();
    
  });

  document.addEventListener('shopify:section:reorder', function () {
        theme.syncRoundedPreviousSections();
        theme.headerGroup.init();
  });
})();
